import { Calendar, ExternalLink, File, FileText, Folder, HardDrive, User } from 'lucide-react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { googleDriveGetFileById } from 'sprites-types/workflow/tools/googleDriveGetFileById'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function GoogleDriveGetFileByIdDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleDriveGetFileById, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data

  const name = (result as any)?.name || (result as any)?.title || 'Unknown file'
  const id = (result as any)?.id || (result as any)?.fileId
  const mimeType = (result as any)?.mimeType || (result as any)?.type || ''
  const size = (result as any)?.size || (result as any)?.fileSize || 0
  const createdTime =
    (result as any)?.createdTime || (result as any)?.created || (result as any)?.dateCreated
  const webViewLink =
    (result as any)?.webViewLink || (result as any)?.viewLink || (result as any)?.url
  const webContentLink = (result as any)?.webContentLink || (result as any)?.downloadLink
  const owners = (result as any)?.owners || (result as any)?.owner ? [(result as any).owner] : []
  const shared = (result as any)?.shared || (result as any)?.isShared || false

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString()
    } catch {
      return dateStr
    }
  }

  const getFileIcon = (mimeType: string) => {
    if (mimeType.includes('folder')) return <Folder className="h-5 w-5 text-blue-500" />
    if (mimeType.includes('document') || mimeType.includes('text'))
      return <FileText className="h-5 w-5 text-blue-600" />
    return <File className="h-5 w-5 text-gray-500" />
  }

  return (
    <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
      <div className="px-4 py-3">
        <div className="flex items-start gap-4">
          <div className="mt-1 flex-shrink-0">
            <div className="rounded-full bg-blue-100 p-2">{getFileIcon(mimeType)}</div>
          </div>
          <div className="min-w-0 flex-1">
            <div className="mb-2 flex items-center gap-2">
              <HardDrive className="h-4 w-4 text-blue-600" />
              <div className="text-sm font-semibold text-gray-900">Google Drive File</div>
              {shared && (
                <div className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-700">
                  Shared
                </div>
              )}
            </div>

            <div className="mb-2 text-sm font-medium text-gray-900">{name}</div>

            <div className="mb-3 grid grid-cols-2 gap-4 text-xs text-gray-600">
              {id && (
                <div>
                  <span className="font-medium">ID:</span> {id}
                </div>
              )}
              {size > 0 && (
                <div>
                  <span className="font-medium">Size:</span>{' '}
                  {formatFileSize(parseInt(size.toString()))}
                </div>
              )}
              {mimeType && (
                <div>
                  <span className="font-medium">Type:</span> {mimeType.split('/').pop() || mimeType}
                </div>
              )}
              {createdTime && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>{formatDate(createdTime)}</span>
                </div>
              )}
            </div>

            {owners.length > 0 && (
              <div className="mb-3 text-xs text-gray-600">
                <div className="mb-1 flex items-center gap-1">
                  <User className="h-3 w-3" />
                  <span className="font-medium">Owner:</span>
                </div>
                {owners.map((owner: any, index: number) => (
                  <div key={index} className="ml-4">
                    {owner.displayName || owner.name || owner.emailAddress}
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3">
              {webViewLink && (
                <a
                  href={webViewLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  View File
                </a>
              )}
              {webContentLink && (
                <a
                  href={webContentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-green-600 hover:text-green-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  Download
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function downloadGoogleDriveGetFileById(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleDriveGetFileByIdToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleDriveGetFileById, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data

  const name = (result as any)?.name || (result as any)?.title || 'Unknown file'
  const id = (result as any)?.id || (result as any)?.fileId
  const mimeType = (result as any)?.mimeType || (result as any)?.type || ''
  const size = (result as any)?.size || (result as any)?.fileSize || 0
  const createdTime =
    (result as any)?.createdTime || (result as any)?.created || (result as any)?.dateCreated
  const webViewLink =
    (result as any)?.webViewLink || (result as any)?.viewLink || (result as any)?.url
  const owners = (result as any)?.owners || (result as any)?.owner ? [(result as any).owner] : []

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  return `## Google Drive File

**Name:** ${name}
${id ? `**ID:** ${id}` : ''}
${mimeType ? `**Type:** ${mimeType}` : ''}
${size > 0 ? `**Size:** ${formatFileSize(parseInt(size.toString()))}` : ''}
${createdTime ? `**Created:** ${new Date(createdTime).toLocaleString()}` : ''}
${owners.length > 0 ? `**Owner:** ${owners.map((o: any) => o.displayName || o.name || o.emailAddress).join(', ')}` : ''}
${webViewLink ? `**Link:** ${webViewLink}` : ''}

---
`
}

export const GoogleDriveGetFileByIdDisplay = createWorkflowToolComponent(
  GoogleDriveGetFileByIdDisplayComponent,
  downloadGoogleDriveGetFileById,
  googleDriveGetFileByIdToMarkdown
)
